import React, { useState } from "react";
import ParrotSlider from "../ParrotSlider";
import OnboardingLayout from "./OnboardingLayout";
import { LanguageLevelOnboarding } from "@shared_types";

interface FluentGoalProps {
  fluent: number;
  setFluent: (fluent: number) => void;
  level: LanguageLevelOnboarding;
}

const FluentGoal: React.FC<FluentGoalProps> = ({
  fluent,
  setFluent,
  level,
}) => {
  const getMinMonths = () => {
    if (level === "i_can_discuss_most_topics_in_detail" || level === "i_can_talk_about_various_topics") {
      return 4;
    }
    return 6;
  };

  const [months, setMonths] = useState(fluent || 12);
  const minMonths = getMinMonths();

  const getColorFromVariable = (variable: string) => {
    const root = document.documentElement;
    return getComputedStyle(root).getPropertyValue(variable).trim();
  };

  const calculateColor = (value: number) => {
    const maxMonths = 24;
    const percentage = (value - minMonths) / (maxMonths - minMonths);
    if (percentage <= 0.33) {
      const mix = percentage * 3;
      return interpolateColors(
        getColorFromVariable("--color-blue"),
        getColorFromVariable("--color-green"),
        mix
      );
    } else if (percentage <= 0.66) {
      const mix = (percentage - 0.33) * 3;
      return interpolateColors(
        getColorFromVariable("--color-green"),
        getColorFromVariable("--color-yellow"),
        mix
      );
    } else {
      const mix = (percentage - 0.66) * 3;
      return interpolateColors(
        getColorFromVariable("--color-yellow"),
        getColorFromVariable("--color-orange"),
        mix
      );
    }
  };

  const interpolateColors = (
    color1: string,
    color2: string,
    factor: number
  ) => {
    const c1 = {
      r: parseInt(color1.slice(1, 3), 16),
      g: parseInt(color1.slice(3, 5), 16),
      b: parseInt(color1.slice(5, 7), 16),
    };
    const c2 = {
      r: parseInt(color2.slice(1, 3), 16),
      g: parseInt(color2.slice(3, 5), 16),
      b: parseInt(color2.slice(5, 7), 16),
    };
    factor = Math.min(1, Math.max(0, factor));
    const r = Math.round(c1.r + (c2.r - c1.r) * factor);
    const g = Math.round(c1.g + (c2.g - c1.g) * factor);
    const b = Math.round(c1.b + (c2.b - c1.b) * factor);
    return `#${r.toString(16).padStart(2, "0")}${g
      .toString(16)
      .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  };

  const handleSliderChange = (value: number) => {
    setMonths(value);
    setFluent(value);
  };

  return (
    <OnboardingLayout
      title="When do you want to be fluent in Spanish by?"
      subtitle="Set your fluency goal. Let's make it realistic."
    >
        <div
          className={`w-44 h-44 rounded-full border-8 flex flex-col items-center justify-center transition-colors mb-4`}
          style={{ borderColor: calculateColor(months) }}
        >
          <div className="text-5xl font-bold">{months}</div>
          <div className="text-xl mt-[-2]">months</div>
        </div>
        <ParrotSlider
          min={minMonths}
          max={24}
          defaultValue={12}
          value={months}
          step={1}
          onChange={handleSliderChange}
          color={calculateColor(months)}
        />
    </OnboardingLayout>
  );
};

export default FluentGoal;
